import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';

import { Activity } from 'app/model/pipeline/activity';
import { Owned } from 'app/model/base/named';
import { DynamicsSink } from 'app/model/pipeline/copysink/dynamics.copysink';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { CopySinkAdapter } from 'app/model/adapters/activities/copysink/copysink.adapter';
import { writeBehaviorOptionValues } from 'app/shared/constants/write-behavior.const';
import { IModel } from 'shell-models-lib';

export const dynamicsSinkAdapts = {
    type: '*',
    writeBehavior: '*',
    writeBatchSize: '*',
    ignoreNullValues: '*',
    maxConcurrentConnections: '*',
    alternateKeyName: '*',
    bypassBusinessLogicExecution: '*',
    bypassPowerAutomateFlows: '*'
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DynamicsSink),
    asynchronous: true,
    adapts: {
        ...dynamicsSinkAdapts,
        structure: '*'
    }
})
export class DynamicsSinkAdapter extends CopySinkAdapter<DynamicsSink, Model.DynamicsSink> {    
    protected model(): IModel {
        return DynamicsSink.MODEL;
    }

    protected override async _toResource(model: DynamicsSink): Promise<Model.DynamicsSink> {
        const result: Model.DynamicsSink = {
            type: this.model().name
        };
        result.writeBatchSize = ValueAdapter.toResource(model, DynamicsSink.WRITE_BATCH_SIZE);
        result.writeBehavior = ValueAdapter.toResource(model, DynamicsSink.WRITE_BEHAVIOR);
        result.ignoreNullValues = ValueAdapter.toResource(model, DynamicsSink.IGNORE_NULL_VALUES);
        result.alternateKeyName = ValueAdapter.toResource(model, DynamicsSink.ALTERNATE_KEY_NAME);
        CopySinkAdapter.maxConcurrentConnectionsToResource(model, result);

        result.bypassBusinessLogicExecution = ValueAdapter.toResource(model, DynamicsSink.BYPASS_BUSINESS_LOGIC_EXECUTION);
        result.bypassPowerAutomateFlows = ValueAdapter.toResource(model, DynamicsSink.BYPASS_POWER_AUTOMATE_FLOWS);

        CopySinkAdapter.toCommonCopySinkResource(model, result, this.context);
        return result;
    }

    protected override async _toModel(resource: Model.DynamicsSink, owner: Activity): Promise<DynamicsSink> {
        const result = Owned.create<DynamicsSink>(this.model(), owner);
        ValueAdapter.toModel(result, DynamicsSink.WRITE_BATCH_SIZE, resource.writeBatchSize);
        ValueAdapter.toModel(result, DynamicsSink.IGNORE_NULL_VALUES, resource.ignoreNullValues);
        ValueAdapter.toModel(result, DynamicsSink.ALTERNATE_KEY_NAME, resource.alternateKeyName);

        // handle upper case and lower case
        let writeBehavior = resource.writeBehavior;
        if (writeBehavior && typeof writeBehavior === 'string') {
            const lowerCaseValue = writeBehavior.toLowerCase();
            switch (lowerCaseValue) {
                case writeBehaviorOptionValues.upsert:
                    writeBehavior = writeBehaviorOptionValues.upsert;
                    break;
                default:
                    break;
            }
        }
        ValueAdapter.toModel(result, DynamicsSink.WRITE_BEHAVIOR, writeBehavior);
        ValueAdapter.toModel(result, DynamicsSink.BYPASS_BUSINESS_LOGIC_EXECUTION, resource.bypassBusinessLogicExecution);
        ValueAdapter.toModel(result, DynamicsSink.BYPASS_POWER_AUTOMATE_FLOWS, resource.bypassPowerAutomateFlows);

        CopySinkAdapter.maxConcurrentConnectionsToModel(resource, result);
        CopySinkAdapter.toCommonCopySinkModel(result, resource);
        return result;
    }
}
